import { createElement, Fragment, type ReactNode } from 'react';

import type { ErrorRow, GroupedError, ImportTask, ImportTaskProgressJson } from './types';

export function statusLabel(status: ImportTask['status']): string {
  if (status === 'Requested') return '実行待ち';
  if (status === 'Started') return '実行中';
  return '完了';
}

export function statusBadgeClass(status: ImportTask['status']): string {
  if (status === 'Requested') return 'bg-subtle text-muted-light';
  if (status === 'Started') return 'bg-notice-light text-notice';
  return 'bg-primary-light text-primary';
}

export function resultLabel(resultType: ImportTask['resultType']): string {
  if (!resultType) return '-';
  if (resultType === 'Succeeded') return '成功';
  if (resultType === 'PartiallyFailed') return '一部失敗';
  return '失敗';
}

export function resultBadgeClass(resultType: ImportTask['resultType']): string {
  if (!resultType) return 'bg-subtle text-muted-light';
  if (resultType === 'Succeeded') return 'bg-positive-light text-positive';
  if (resultType === 'PartiallyFailed') return 'bg-warning-light text-warning';
  return 'bg-negative-light text-negative';
}

export function groupErrors(errors: ErrorRow[]): GroupedError[] {
  const map = new Map<string, GroupedError>();
  for (const e of errors) {
    const key = `${e.column}\u0001${e.message}`;
    const existing = map.get(key);
    if (existing) {
      existing.count += 1;
      existing.rows.push(e.row);
    } else {
      map.set(key, { column: e.column, message: e.message, count: 1, rows: [e.row] });
    }
  }
  return [...map.values()].sort((a, b) => b.count - a.count);
}

function emphasize(value: string): ReactNode {
  return createElement('span', { className: 'font-semibold' }, value);
}

/**
 * バックエンドが返すエラーメッセージは英語の技術用語が中心のため、
 * 一般ユーザー（非エンジニア）にも分かるように日本語表現へ置き換える。
 * パターンにマッチしないものは原文のままにし、情報が失われないようにする。
 */
export function humanizeErrorMessage(message: string, contractUkColumns: string[] | undefined): ReactNode {
  const trimmed = message.trim();

  if (trimmed === 'Contract composite key is empty') {
    const cols = contractUkColumns && contractUkColumns.length > 0 ? `（${contractUkColumns.join('、')}）` : '';
    return createElement(
      Fragment,
      null,
      `契約を特定するためのキー項目${cols}が`,
      emphasize('すべて空欄です'),
      '。CSV の該当列に値が入っているかをご確認ください。',
    );
  }

  const missingHeaders = trimmed.match(/^Missing required columns in CSV header: (.+)$/);
  if (missingHeaders) {
    return createElement(
      Fragment,
      null,
      'CSV のヘッダー行に必須の列',
      emphasize(`「${missingHeaders[1]}」`),
      'が見つかりません。列名が一致しているかをご確認ください。',
    );
  }

  const invalidColumnType = trimmed.match(/^Invalid ImportTableDefinition\.ColumnType: (.+)$/);
  if (invalidColumnType) {
    return createElement(
      Fragment,
      null,
      '列の型',
      emphasize(`「${invalidColumnType[1]}」`),
      'が正しくありません。テーブル定義をご確認ください。',
    );
  }

  const invalidEncoding = trimmed.match(/^Invalid ImportTableDefinition\.Encoding: (.+)$/);
  if (invalidEncoding) {
    return createElement(
      Fragment,
      null,
      'CSV の文字コード設定',
      emphasize(`「${invalidEncoding[1]}」`),
      'が正しくありません。テーブル定義をご確認ください。',
    );
  }

  const invalidDelimiter = trimmed.match(/^Invalid ImportTableDefinition\.Delimiter: (.+)$/);
  if (invalidDelimiter) {
    return createElement(
      Fragment,
      null,
      'CSV の区切り文字設定',
      emphasize(`「${invalidDelimiter[1]}」`),
      'が正しくありません。テーブル定義をご確認ください。',
    );
  }

  const invalidIdentifierType = trimmed.match(/^Invalid Identifier\.Type: (.+?)(?: \(allowed:.*\))?$/);
  if (invalidIdentifierType) {
    return createElement(
      Fragment,
      null,
      '顧客の識別子タイプ',
      emphasize(`「${invalidIdentifierType[1]}」`),
      'が使用できない形式です。テーブル定義の顧客一意キーの設定をご確認ください。',
    );
  }

  if (/^ImportTask not found:/i.test(trimmed)) {
    return createElement(Fragment, null, '対象の', emphasize('タスク情報'), 'が見つかりませんでした。時間をおいて再度お試しください。');
  }
  if (/^FileMetadata not found:/i.test(trimmed)) {
    return createElement(
      Fragment,
      null,
      emphasize('アップロードしたファイル'),
      'が見つかりませんでした。再度ファイルをアップロードしてからお試しください。',
    );
  }
  if (/^ImportTableDefinition not found:/i.test(trimmed)) {
    return createElement(Fragment, null, emphasize('テーブル定義'), 'が見つかりませんでした。削除されていないかをご確認ください。');
  }

  return trimmed;
}

export function columnDisplay(column: string, contractUkColumns: string[] | undefined): string {
  if (column.trim().length > 0) return column;
  if (contractUkColumns && contractUkColumns.length > 0) return contractUkColumns.join('、');
  return '行全体 / 列不明';
}

export function rowsPreview(rows: number[]): string {
  const sorted = [...rows].sort((a, b) => a - b);
  const limit = 20;
  if (sorted.length <= limit) return sorted.join('、');
  return `${sorted.slice(0, limit).join('、')} …他${sorted.length - limit}件`;
}

const destinationJa = (d: string): string => {
  if (d === 'customer') return '顧客';
  if (d === 'contract') return '契約';
  if (d === 'ignore') return '無視';
  return d;
};

/**
 * 互換性チェックが列に付与する英語の理由文字列を、タスク詳細サマリー向けに日本語へ寄せる。
 */
export function humanizeColumnChangeReason(reason: string): string {
  const t = reason.trim();
  const typeM = t.match(/^type ([^ ]+) -> (.+)$/);
  if (typeM) return `型の変更（${typeM[1]} → ${typeM[2]}）`;
  const destM = t.match(/^destination ([^ ]+) -> (.+)$/);
  if (destM) return `格納先の変更（${destinationJa(destM[1])} → ${destinationJa(destM[2])}）`;
  if (t === 'required false -> true') return '任意列から必須列への変更';
  const multiM = t.match(/^multiValued (true|false) -> (true|false)$/);
  if (multiM) {
    const from = multiM[1] === 'true' ? '複数値あり' : '単一値';
    const to = multiM[2] === 'true' ? '複数値あり' : '単一値';
    return `複数値設定の変更（${from} → ${to}）`;
  }
  return t;
}

/**
 * revision に保存された otherChanges（互換性チェックの breakingReasons 由来）を日本語化する。
 */
export function humanizeRevisionOtherChange(reason: string): string {
  const t = reason.trim();

  if (t === 'contract uniqueKeys changed') return '契約の一意キー設定が変更されました';
  if (t === 'customer uniqueKeys presence changed') return '顧客の一意キー設定の有無が変更されました';
  if (t === 'customer uniqueKeys changed') return '顧客の一意キー設定が変更されました';

  const reqAdded = t.match(/^required columns added: (.+)$/);
  if (reqAdded) return `必須列として追加: ${reqAdded[1]}`;

  const removed = t.match(/^removed columns: (.+)$/);
  if (removed) return `列の削除: ${removed[1]}`;

  const loadM = t.match(/^loadMode (.+) -> (.+)$/);
  if (loadM) return `取込モードの変更（${loadM[1]} → ${loadM[2]}）`;

  const imM = t.match(/^importMethod (.+) -> (.+)$/);
  if (imM) return `取込経路の変更（${imM[1]} → ${imM[2]}）`;

  const encM = t.match(/^encoding (.+) -> (.+)$/);
  if (encM) return `文字コードの変更（${encM[1]} → ${encM[2]}）`;

  const delimM = t.match(/^delimiter (.+) -> (.+)$/);
  if (delimM) return `区切り文字の変更（${delimM[1]} → ${delimM[2]}）`;

  const hdrM = t.match(/^hasHeaderRow (.+) -> (.+)$/);
  if (hdrM) return `ヘッダー行の有無の変更（${hdrM[1]} → ${hdrM[2]}）`;

  if (t.startsWith('changed columns: ')) return `列定義の変更: ${t.slice('changed columns: '.length)}`;

  return t;
}

/** 経過時間と処理行数から残り時間（ms）を線形外挿で見積もる。算出不能なら undefined。 */
export function estimateRemainingDurationMs(params: {
  startedAt: ImportTask['startedAt'];
  progress: ImportTaskProgressJson;
  nowMs?: number;
}): number | undefined {
  const { startedAt, progress, nowMs = Date.now() } = params;
  const { processedRows, totalRows, phase } = progress;

  if (phase === 'sweeping') return undefined;
  if (!startedAt || totalRows === undefined || totalRows <= 0) return undefined;
  if (processedRows <= 0 || processedRows >= totalRows) return undefined;

  const elapsedMs = nowMs - startedAt.valueOf();
  if (elapsedMs <= 0) return undefined;

  const rowsPerMs = processedRows / elapsedMs;
  const remainingRows = totalRows - processedRows;
  return remainingRows / rowsPerMs;
}

export function formatRemainingDuration(ms: number): string {
  const totalSeconds = Math.max(1, Math.ceil(ms / 1000));
  if (totalSeconds < 60) return `約 ${totalSeconds} 秒`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) {
    return seconds > 0 ? `約 ${minutes} 分 ${seconds} 秒` : `約 ${minutes} 分`;
  }

  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return remMinutes > 0 ? `約 ${hours} 時間 ${remMinutes} 分` : `約 ${hours} 時間`;
}

export function remainingTimeLabel(progress: ImportTaskProgressJson, startedAt: ImportTask['startedAt']): string {
  if (progress.phase === 'sweeping') {
    return '予測残り時間: 削除処理中（算出できません）';
  }

  // apply 前の前段フェーズ (load/staging/diff/plan) は行レベルの進捗が無いため残り時間を外挿できない。
  if (progress.phase === 'loading' || progress.phase === 'staging' || progress.phase === 'diffing' || progress.phase === 'planning') {
    return '予測残り時間: 取込準備中（算出できません）';
  }

  const remainingMs = estimateRemainingDurationMs({ startedAt, progress });
  if (remainingMs !== undefined) {
    return `予測残り時間: ${formatRemainingDuration(remainingMs)}`;
  }

  if (progress.totalRows !== undefined && progress.processedRows >= progress.totalRows) {
    return '予測残り時間: まもなく完了';
  }

  return '予測残り時間: 算出中…';
}
